import React, { useState, useEffect, useContext } from 'react'
import { useNotify } from '../components/notice/useNotify'
import { UserContext } from '../components/UserContext'
import PersonaCard from '../components/PersonaCard'
import PersonaCreator from '../components/PersonaCreator'
import CreateAdvisorCard from '../components/CreateAdvisorCard'
import PersonaSearch from '../components/PersonaSearch'
import {
  getPersonas,
  addPersonaToSquad,
  removePersonaFromSquad,
  getSquadPersonas,
  Persona,
  SquadPersonas
} from '../api/client'

const SquadPage: React.FC = () => {
  const notify = useNotify()
  const { user } = useContext(UserContext)
  const [squadPersonas, setSquadPersonas] = useState<SquadPersonas>({
    account_squad: [],        // This will contain user's squad
    friends_and_family: [],   // This will contain friends and family personas
    top_life_advisors: []     // This will contain top life advisors
  })
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'squad' | 'all'>('all')
  const [showAdvisorCreator, setShowAdvisorCreator] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [categoryPersonas, setCategoryPersonas] = useState<{ [key: string]: { name: string; personas: Persona[] } }>({})
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)
  const [searchResults, setSearchResults] = useState<Persona[]>([])
  
  // New state for the different sections
  const [friendsAndFamily, setFriendsAndFamily] = useState<Persona[]>([])
  const [bestLifeAdvisors, setBestLifeAdvisors] = useState<Persona[]>([])
  const [friendsAndFamilyVisible, setFriendsAndFamilyVisible] = useState(20)
  const [bestLifeVisible, setBestLifeVisible] = useState(20)
  

  // Fetch squad personas on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        // Use the new dedicated squad personas endpoint
        const personas = await getSquadPersonas()
        setSquadPersonas(personas)
        
        // Use the backend-categorized data directly
        setFriendsAndFamily(personas.friends_and_family)
        setBestLifeAdvisors(personas.top_life_advisors)
        
        setLoading(false)
      } catch (err) {
        notify.error('Failed to load personas')
        console.error(err)
        setLoading(false)
      }
    }

    fetchData()
  }, [])
  

 const handleAddToSquad = async (persona: Persona) => {
    try {
      // Call the actual API endpoint
      await addPersonaToSquad(persona.id)

      // Refresh squad personas to get updated state
      const updatedPersonas = await getSquadPersonas()
      setSquadPersonas(updatedPersonas)
      
      // Update the categorized data using the data from getSquadPersonas
      const squadPersonaIds = new Set(updatedPersonas.account_squad.map(p => p.id))
      setFriendsAndFamily(updatedPersonas.friends_and_family)
      setBestLifeAdvisors(updatedPersonas.top_life_advisors)
      // Only recategorize remaining advisors if we have them loaded
      if (categoriesLoaded) {
        const allPersonas = await getPersonas()
        const alreadyShownIds = new Set([
          ...squadPersonaIds,
          ...updatedPersonas.friends_and_family.map(p => p.id),
          ...updatedPersonas.top_life_advisors.map(p => p.id)
        ])
        const remainingPersonas = allPersonas.filter(p => !alreadyShownIds.has(p.id))
        const categories = groupPersonasByCategory(remainingPersonas)
        setCategoryPersonas(categories)
      }
      
      // Reset categories loaded state
      setCategoriesLoaded(false)
      setCategoryPersonas({})
      
      // Also remove from search results if present
      if (searchResults.length > 0) {
        setSearchResults(searchResults.filter(p => p.id !== persona.id))
      }
      
      notify.success(`Added ${persona.first_name} to your squad!`)
    } catch (err) {
      notify.error(`Failed to add ${persona.first_name} to your squad`)
      console.error(err)
    }
  }

  const handleRemoveFromSquad = async (personaId: number) => {
    try {
      // Find the persona name before removing it
      const personaToRemove = squadPersonas.account_squad.find(p => p.id === personaId)
      
      // Call the actual API endpoint
      await removePersonaFromSquad(personaId)

      // Refresh squad personas to get updated state
      const updatedPersonas = await getSquadPersonas()
      setSquadPersonas(updatedPersonas)
      
      // Update the categorized data using the data from getSquadPersonas
      const squadPersonaIds = new Set(updatedPersonas.account_squad.map(p => p.id))
      setFriendsAndFamily(updatedPersonas.friends_and_family)
      setBestLifeAdvisors(updatedPersonas.top_life_advisors)
      // Only recategorize remaining advisors if we have them loaded
      if (categoriesLoaded) {
        const allPersonas = await getPersonas()
        const alreadyShownIds = new Set([
          ...squadPersonaIds,
          ...updatedPersonas.friends_and_family.map(p => p.id),
          ...updatedPersonas.top_life_advisors.map(p => p.id)
        ])
        const remainingPersonas = allPersonas.filter(p => !alreadyShownIds.has(p.id))
        const categories = groupPersonasByCategory(remainingPersonas)
        setCategoryPersonas(categories)
      }
      
      // Reset categories loaded state
      setCategoriesLoaded(false)
      setCategoryPersonas({})
      
      if (personaToRemove) {
        notify.success(`Removed ${personaToRemove.first_name} from your squad`)
      } else {
        notify.success('Removed advisor from your squad')
      }
    } catch (err) {
      notify.error('Failed to remove advisor from your squad')
      console.error(err)
    }
  }

  // This function is now obsolete since nickname is stored directly on the persona
  // and is updated using the persona edit form

  const isInSquad = (personaId: number) => {
    return squadPersonas.account_squad.some(p => p.id === personaId || p.parent_persona_id === personaId)
  }

  // Load remaining personas when showing categories
  const loadRemainingPersonas = async () => {
    if (categoriesLoaded || loadingCategories) return
    
    setLoadingCategories(true)
    try {
      // Now we actually fetch all personas to get the complete list
      const allPersonas = await getPersonas()
      const squadPersonaIds = new Set(squadPersonas.account_squad.map(p => p.id))
      
      // Filter out personas that are already shown in friends/family and bestLifeAdvisors sections
      const alreadyShownIds = new Set([
        ...squadPersonaIds,
        ...friendsAndFamily.map(p => p.id),
        ...bestLifeAdvisors.map(p => p.id)
      ])
      
      const remainingPersonas = allPersonas.filter(p => !alreadyShownIds.has(p.id))
      
      // Group remaining advisors by categories
      const categories = groupPersonasByCategory(remainingPersonas)
      setCategoryPersonas(categories)
      setCategoriesLoaded(true)
    } catch (err) {
      notify.error('Failed to load additional advisors')
      console.error(err)
    } finally {
      setLoadingCategories(false)
    }
  }

  // Group personas by their expertise categories
  const groupPersonasByCategory = (personas: Persona[]) => {
    const categories: { [key: string]: { name: string; personas: Persona[] } } = {}
    const uncategorized: Persona[] = []
    
    personas.forEach(persona => {
      if (!persona.expertise || persona.expertise.length === 0) {
        uncategorized.push(persona)
        return
      }
      
      // Use the primary expertise category (first one)
      const primaryCategory = persona.expertise[0]
      const categoryKey = primaryCategory.category_slug
      
      if (!categories[categoryKey]) {
        categories[categoryKey] = {
          name: primaryCategory.category_name,
          personas: []
        }
      }
      
      categories[categoryKey].personas.push(persona)
    })
    
    if (uncategorized.length > 0) {
      categories['general'] = {
        name: 'General',
        personas: uncategorized
      }
    }
    
    return categories
  }

  const toggleCategory = (categoryKey: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey)
    } else {
      newExpanded.add(categoryKey)
    }
    setExpandedCategories(newExpanded)
  }

  // Handler for persona updates
  const handlePersonaUpdated = async () => {
    try {
      // Refresh squad personas to get updated state
      const updatedPersonas = await getSquadPersonas()
      setSquadPersonas(updatedPersonas)
      
      // Update the categorized data using the data from getSquadPersonas
      const squadPersonaIds = new Set(updatedPersonas.account_squad.map(p => p.id))
      setFriendsAndFamily(updatedPersonas.friends_and_family)
      setBestLifeAdvisors(updatedPersonas.top_life_advisors)
      // Only recategorize remaining advisors if we have them loaded
      if (categoriesLoaded) {
        const allPersonas = await getPersonas()
        const alreadyShownIds = new Set([
          ...squadPersonaIds,
          ...updatedPersonas.friends_and_family.map(p => p.id),
          ...updatedPersonas.top_life_advisors.map(p => p.id)
        ])
        const remainingPersonas = allPersonas.filter(p => !alreadyShownIds.has(p.id))
        const categories = groupPersonasByCategory(remainingPersonas)
        setCategoryPersonas(categories)
      }
      
      // Reset categories loaded state
      setCategoriesLoaded(false)
      setCategoryPersonas({})
    } catch (err) {
      notify.error('Failed to update the squad with the updated advisor');
      console.error(err);
    }
  };
  
  const handleAdvisorCreated = async (persona: Persona) => {
    try {
      // Refresh squad personas to get updated state
      const updatedPersonas = await getSquadPersonas()
      setSquadPersonas(updatedPersonas)
      
      // Update the categorized data using the data from getSquadPersonas
      const squadPersonaIds = new Set(updatedPersonas.account_squad.map(p => p.id))
      setFriendsAndFamily(updatedPersonas.friends_and_family)
      setBestLifeAdvisors(updatedPersonas.top_life_advisors)
      // Only recategorize remaining advisors if we have them loaded
      if (categoriesLoaded) {
        const allPersonas = await getPersonas()
        const alreadyShownIds = new Set([
          ...squadPersonaIds,
          ...updatedPersonas.friends_and_family.map(p => p.id),
          ...updatedPersonas.top_life_advisors.map(p => p.id)
        ])
        const remainingPersonas = allPersonas.filter(p => !alreadyShownIds.has(p.id))
        const categories = groupPersonasByCategory(remainingPersonas)
        setCategoryPersonas(categories)
      }
      
      // Reset categories loaded state
      setCategoriesLoaded(false)
      setCategoryPersonas({})
      
      notify.success(`Successfully created advisor ${persona.first_name}`)
      
      // Switch to squad tab to show the newly added advisor
      setActiveTab('squad')
    } catch (err) {
      notify.error('Failed to update squad with the new advisor')
      console.error(err)
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      {/* Advisor Creator Modal */}
      <PersonaCreator
        isOpen={showAdvisorCreator}
        onClose={() => setShowAdvisorCreator(false)}
        onPersonaCreated={handleAdvisorCreated}
        isAdmin={user?.isAdmin}
      />
      
      <div className="container mx-auto px-4 pb-8 sm:pt-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">
            Team {user?.firstName || 'Your'}
          </h1>
          
          <div className="flex items-center gap-4">
            <div className="flex space-x-2 bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => {
                  setActiveTab('squad')
                  setSearchResults([])
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'squad'
                    ? 'bg-white text-amber-800 shadow'
                    : 'text-gray-700'
                }`}
              >
                My Squad
              </button>
              <button
                onClick={() => {
                  setActiveTab('all')
                  setSearchResults([])
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'all'
                    ? 'bg-white text-amber-800 shadow'
                    : 'text-gray-700'
                }`}
              >
                Available Advisors
              </button>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : activeTab === 'squad' ? (
          <>
            {squadPersonas.account_squad.length === 0 ? (
              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <h3 className="text-xl text-gray-700 mb-2">Your squad is empty</h3>
                <p className="text-gray-500 mb-4">
                  Add advisors to your squad to get personalized advice and guidance.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <button
                    onClick={() => setActiveTab('all')}
                    className="mt-2 bg-white border border-amber-600 text-amber-800 px-4 py-2 rounded-md hover:bg-amber-50"
                  >
                    Browse Available Advisors
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {squadPersonas.account_squad.map(persona => (
                  <div key={persona.id} className="flex flex-col">
                    <PersonaCard
                      persona={persona}
                      onPersonaUpdated={handlePersonaUpdated}
                      isAdmin={user?.isAdmin}
                      editActionButtons={
                        <button
                          onClick={() => handleRemoveFromSquad(persona.id)}
                          className="px-3 py-2 bg-rose-100 text-rose-700 rounded-md hover:bg-rose-200"
                        >
                          Remove
                        </button>
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Search Section */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <PersonaSearch 
                  onSearchResults={setSearchResults}
                />
              </div>
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-700 mb-4">Search Results ({searchResults.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {searchResults.map(persona => {
                    const isPersonaInSquad = isInSquad(persona.id)
                    return (
                      <PersonaCard
                        key={persona.id}
                        persona={persona}
                        onPersonaUpdated={handlePersonaUpdated}
                        isAdmin={user?.isAdmin}
                        isInSquad={isPersonaInSquad}
                        actionButtons={
                          !isPersonaInSquad && (
                            <button
                              onClick={() => handleAddToSquad(persona)}
                              className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                            >
                              Add to Squad
                            </button>
                          )
                        }
                      />
                    )
                  })}
                </div>
              </div>
            )}
            
            
            {/* Friends and Family Section - hide when searching */}
            {searchResults.length === 0 && friendsAndFamily.length > 0 && (
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-700">Friends and Family</h3>
                  {friendsAndFamily.length > 20 && (
                    <button
                      onClick={() => setFriendsAndFamilyVisible(prev => prev === 20 ? friendsAndFamily.length : 20)}
                      className="text-amber-800 hover:text-amber-900 text-sm font-medium"
                    >
                      {friendsAndFamilyVisible === 20 ? `Load More (${friendsAndFamily.length - 20} more)` : 'Show Less'}
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {friendsAndFamily.slice(0, friendsAndFamilyVisible).map(persona => (
                    <PersonaCard
                      key={persona.id}
                      persona={persona}
                      onPersonaUpdated={handlePersonaUpdated}
                      isAdmin={user?.isAdmin}
                      actionButtons={
                        <button
                          onClick={() => handleAddToSquad(persona)}
                          className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                        >
                          Add to Squad
                        </button>
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Top Life Advisors Section - hide when searching */}
            {searchResults.length === 0 && (
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-700">Top Life Advisors</h3>
                  {bestLifeAdvisors.length > 20 && (
                    <button
                      onClick={() => setBestLifeVisible(prev => prev === 20 ? bestLifeAdvisors.length : 20)}
                      className="text-amber-800 hover:text-amber-900 text-sm font-medium"
                    >
                      {bestLifeVisible === 20 ? `Load More (${bestLifeAdvisors.length - 20} more)` : 'Show Less'}
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <CreateAdvisorCard onClick={() => setShowAdvisorCreator(true)} />
                  
                  {bestLifeAdvisors.slice(0, bestLifeVisible).map(persona => (
                    <PersonaCard
                      key={persona.id}
                      persona={persona}
                      onPersonaUpdated={handlePersonaUpdated}
                      isAdmin={user?.isAdmin}
                      actionButtons={
                        <button
                          onClick={() => handleAddToSquad(persona)}
                          className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                        >
                          Add to Squad
                        </button>
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Advisors Section - organized by categories, hide when searching */}
            {searchResults.length === 0 && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-700">All Advisors</h3>
                  {!categoriesLoaded && (
                    <button
                      onClick={loadRemainingPersonas}
                      disabled={loadingCategories}
                      className={`px-4 py-2 text-sm font-medium rounded-md border ${
                        loadingCategories
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-amber-800 border-amber-600 hover:bg-amber-50'
                      }`}
                    >
                      {loadingCategories ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading...
                        </>
                      ) : (
                        'Load More Advisors'
                      )}
                    </button>
                  )}
                </div>
                
                {categoriesLoaded && Object.keys(categoryPersonas).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(categoryPersonas).map(([categoryKey, categoryData]) => (
                      <div key={categoryKey} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleCategory(categoryKey)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center">
                            <span className="text-lg font-medium text-gray-800">{categoryData.name}</span>
                            <span className="ml-2 text-sm text-gray-500">({categoryData.personas.length})</span>
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-400 transform transition-transform ${
                              expandedCategories.has(categoryKey) ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {expandedCategories.has(categoryKey) && (
                          <div className="px-4 pb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {categoryData.personas.map(persona => (
                                <PersonaCard
                                  key={persona.id}
                                  persona={persona}
                                  onPersonaUpdated={handlePersonaUpdated}
                                  isAdmin={user?.isAdmin}
                                  actionButtons={
                                    <button
                                      onClick={() => handleAddToSquad(persona)}
                                      className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                                    >
                                      Add to Squad
                                    </button>
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : categoriesLoaded && Object.keys(categoryPersonas).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No additional advisors available in categories.</p>
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SquadPage